// CONTRACT:C4-DESIGN-TOKENS.1.0
// COP — theme application.
//
// Applies the bespoke INTEL_SKIN command-grade theme (CONTRACT:C4-DESIGN-TOKENS.1.0)
// by emitting its primitive→CSS-variable map and setting every variable on :root.
// The shell, HUD, panels and the deck.gl affiliation symbology all read from these
// variables, so a theme swap reskins everything with no recompile. INTEL_SKIN is a
// refined dark tactical look (denser, higher-contrast, a single TEAL SECURE SPINE
// accent #1FB6B6 — policy/key-access/AI/selection); the status-* affiliation slots
// remain the shared APP-6 source of truth unchanged (teal is never a map marker hue).

import {
  INTEL_SKIN_THEME,
  LIGHT_THEME,
  CIVIC_THEME,
  toCssVariables,
  resolveStatusColor,
} from "../../../../packages/design-tokens/src/index.ts";
// Direct module import (not the barrel): the label-plate token key is a shipped-theme extra,
// not part of the C4 semantic-slot surface the barrel exports.
import { LABEL_PLATE_TOKEN } from "../../../../packages/design-tokens/src/themes.ts";
import type { Theme } from "../../../../packages/contracts-ts/src/index.ts";
import type { Affiliation } from "../../../../packages/schema/ts/envelope.ts";
import type { AffiliationColors } from "./deckBackend.ts";

/** The COP's active theme — the bespoke command-grade INTEL_SKIN. */
export const COP_THEME: Theme = INTEL_SKIN_THEME;

/** Set the theme's CSS variables on :root. Returns the applied theme. Also stamps
 *  `data-theme="<id>"` on the root element so theme-conditional CSS (e.g. the LIGHT
 *  basemap's map-control icon treatment in style.css) can key off the active theme —
 *  this mirrors the existing `data-force-reduced-motion` root-attribute convention and
 *  is render-inert for the dark default (no rule keys off the dark ids). */
export function applyTheme(theme: Theme = COP_THEME): Theme {
  const vars = toCssVariables(theme);
  const root = document.documentElement;
  for (const [name, value] of Object.entries(vars)) {
    root.style.setProperty(name, value);
  }
  root.setAttribute("data-theme", theme.id);
  return theme;
}

/** Basemap style URLs (CARTO, no API key). The dark-matter instrument basemap is the resting
 *  (dark / night / intel) ground; CARTO Positron is the TRUE LIGHT basemap for the 'day' profile.
 *  DARK_BASEMAP_URL mirrors the MAP_STYLE constant in cop.ts (kept in sync deliberately). */
export const DARK_BASEMAP_URL =
  "https://basemaps.cartocdn.com/gl/dark-matter-gl-style/style.json";
export const LIGHT_BASEMAP_URL =
  "https://basemaps.cartocdn.com/gl/positron-gl-style/style.json";

/** The LIGHT-SURFACE theme ids — these pair with the CARTO Positron (light) basemap so panel glass
 *  and the ground read together (no dark map under light chrome). The dark-family themes (dark,
 *  intel-skin, mono-nvg, high-contrast, tak) ride the dark-matter instrument basemap. Adding a new
 *  light theme means adding its id here (keeps the basemap swap a single, enumerable decision). */
export const LIGHT_PROFILE_IDS: ReadonlySet<string> = new Set([LIGHT_THEME.id, CIVIC_THEME.id]);

/** The MapLibre basemap style URL a render profile expects: the LIGHT (Positron) basemap for a
 *  light-surface theme (LIGHT 'day' / CIVIC consumer), the dark-matter instrument basemap for every
 *  dark-family profile. cop.ts setRenderProfile uses this to swap map.setStyle() alongside applyTheme(). */
export function basemapUrlForProfile(themeId: string): string {
  return LIGHT_PROFILE_IDS.has(themeId) ? LIGHT_BASEMAP_URL : DARK_BASEMAP_URL;
}

/** The :root attribute that forces reduced-motion regardless of the OS media query (mirrored by a
 *  CSS rule in settings.css / style.css). Shared between the SettingsDrawer toggle and the boot
 *  re-apply, so the persisted reduced-motion preference is honoured the same way everywhere. */
export const REDUCED_MOTION_ATTR = "data-force-reduced-motion";

/** Apply (or clear) the force-reduced-motion :root attribute. UI-only — no host/engine coupling.
 *  Used by the SettingsDrawer on toggle and by the boot re-apply of persisted settings. */
export function applyReducedMotion(on: boolean): void {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  if (on) root.setAttribute(REDUCED_MOTION_ATTR, "on");
  else root.removeAttribute(REDUCED_MOTION_ATTR);
}

/** Resolve the single-source affiliation palette from the active theme. The
 *  design-tokens status slots map the canonical P1 affiliations; "friend" maps
 *  to the "status-friendly" slot. We resolve PRIMITIVE hexes here because deck.gl
 *  needs concrete colors, not CSS-var references. */
export function affiliationColorsFor(theme: Theme = COP_THEME): AffiliationColors {
  const resolve = (slot: Parameters<typeof resolveStatusColor>[1]): string =>
    resolveStatusColor(theme, slot, { fallback: true });
  const colors: AffiliationColors = {
    friend: resolve("status-friendly"),
    hostile: resolve("status-hostile"),
    neutral: resolve("status-neutral"),
    unknown: resolve("status-unknown"),
    pending: resolve("status-pending"),
    // PROTECTIVE LABEL PLATE — the per-theme backing colour behind map callsign labels
    // (deckBackend buildCallsigns). Rides THIS palette seam deliberately: cop.ts
    // setRenderProfile already calls backend.setAffiliationColors(affiliationColorsFor(next)),
    // so a runtime profile switch re-points the plate in place exactly like the marker palette
    // with no extra host wiring. A theme without the token (external/coalition skin) leaves it
    // undefined — the backend falls back to the near-black dark-family plate (fail-to-default).
    labelPlate: theme.tokens[LABEL_PLATE_TOKEN],
  };
  return colors;
}

/** Canonical affiliation order for the legend UI. */
export const AFFILIATIONS: readonly Affiliation[] = [
  "friend",
  "hostile",
  "neutral",
  "unknown",
  "pending",
];
